import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Megaphone, Upload, Loader2, Check, ExternalLink, Sparkles, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import { api, apiError, imgUrl } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";

const STATUS_STYLES = {
  active: "bg-green-100 text-green-700",
  pending_payment: "bg-amber-100 text-amber-700",
  expired: "bg-neutral-200 text-neutral-600",
};

export default function Advertise() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [authOpen, setAuthOpen] = useState(false);
  const [plansData, setPlansData] = useState({ plans: {}, per_products: 5 });
  const [myProducts, setMyProducts] = useState([]);
  const [myAds, setMyAds] = useState([]);

  const [adType, setAdType] = useState("product");
  const [plan, setPlan] = useState("30");
  const [productId, setProductId] = useState("");
  const [title, setTitle] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  usePageMeta(
    "Advertise on Beauty Trade Club — Sponsored Placements",
    "Promote your product or brand to an engaged beauty audience. Self-serve sponsored placements in the Beauty Trade Club shop grid, starting at $9.99.",
    "/advertise"
  );

  const loadMine = useCallback(() => {
    if (!user) return;
    api.get("/my/products").then(({ data }) => setMyProducts(data.filter((p) => p.status === "active"))).catch(() => {});
    api.get("/my/ads").then(({ data }) => setMyAds(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/ads/plans").then(({ data }) => setPlansData(data)).catch(() => {});
  }, []);

  useEffect(() => { loadMine(); }, [loadMine]);

  // Return from Stripe checkout
  useEffect(() => {
    const session = params.get("ad_session");
    if (!session || !user) return;
    api.get(`/ads/status/${session}`)
      .then(({ data }) => {
        if (data.status === "active") toast.success("Your ad is live! 🎉 It's now showing in the shop.");
        else toast.info("Payment received — your ad will go live shortly.");
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => { setParams({}); loadMine(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImage(data.path);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (adType === "product" && !productId) { toast.error("Pick one of your listings to promote"); return; }
    if (adType === "external") {
      if (!title.trim()) { toast.error("Add a title for your ad"); return; }
      if (!image) { toast.error("Upload a banner image"); return; }
      if (!targetUrl.trim().startsWith("http")) { toast.error("Add a destination link starting with http://"); return; }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/ads/checkout", {
        origin_url: window.location.origin,
        ad_type: adType,
        plan,
        title: title.trim(),
        image_url: image,
        target_url: targetUrl.trim(),
        product_id: productId,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
      setSubmitting(false);
    }
  };

  const plans = plansData.plans || {};
  const standard = Object.entries(plans).filter(([, p]) => p.tier !== "brand");
  const brand = Object.entries(plans).filter(([, p]) => p.tier === "brand");

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="advertise-page">
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />

      <div className="text-center mb-10">
        <div className="flex items-center justify-center gap-2 text-[#E8956B] text-sm font-extrabold uppercase tracking-[0.3em] mb-3">
          <Megaphone size={16} /> Advertise
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 tracking-tight">
          Put your product in front of <span className="text-[#E8956B]">beauty lovers</span>
        </h1>
        <p className="mt-4 text-base text-neutral-500 max-w-2xl mx-auto">
          Sponsored placements appear as cards right inside our shop grid — seen by shoppers actively
          browsing to buy. We keep it premium: no more than 1 in every {plansData.per_products} cards is an ad.
        </p>
      </div>

      {/* PLANS */}
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div>
          <h2 className="font-display text-lg font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Sparkles size={18} className="text-[#E8956B]" /> Self-serve
          </h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {standard.map(([key, p]) => (
              <button
                key={key}
                data-testid={`plan-${key}`}
                onClick={() => setPlan(key)}
                className={`text-left rounded-2xl border-2 p-4 transition-all ${plan === key ? "border-[#E8956B] bg-[#E8956B]/5" : "border-neutral-200 hover:border-neutral-300"}`}
              >
                <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{p.label}</div>
                <div className="text-2xl font-black text-neutral-900 mt-1">${p.price}</div>
                <div className="text-xs text-neutral-500 mt-1">{p.days} days</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display text-lg font-bold text-neutral-900 mb-3 flex items-center gap-2">
            <Crown size={18} className="text-[#E8956B]" /> Brand (top-of-grid priority)
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {brand.map(([key, p]) => (
              <button
                key={key}
                data-testid={`plan-${key}`}
                onClick={() => setPlan(key)}
                className={`text-left rounded-2xl border-2 p-4 transition-all relative overflow-hidden ${plan === key ? "border-[#E8956B] bg-[#E8956B]/5" : "border-neutral-200 hover:border-neutral-300"}`}
              >
                <span className="absolute top-0 right-0 bg-[#E8956B] text-white text-[9px] font-bold px-2 py-0.5 rounded-bl-lg tracking-wider">PRIORITY</span>
                <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{p.label}</div>
                <div className="text-2xl font-black text-neutral-900 mt-1">${p.price}</div>
                <div className="text-xs text-neutral-500 mt-1">{p.days} days · shown first</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* BUILDER */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-6 sm:p-8 mb-14">
        <h2 className="font-display text-xl font-bold text-neutral-900 mb-5">Create your ad</h2>

        <div className="flex gap-3 mb-6">
          <button
            data-testid="adtype-product"
            onClick={() => setAdType("product")}
            className={`flex-1 rounded-xl border-2 p-4 text-left transition-all ${adType === "product" ? "border-[#E8956B] bg-white" : "border-neutral-200 bg-white hover:border-neutral-300"}`}
          >
            <div className="font-bold text-neutral-900 text-sm">Promote my listing</div>
            <div className="text-xs text-neutral-500 mt-1">Boost one of your BTC products.</div>
          </button>
          <button
            data-testid="adtype-external"
            onClick={() => setAdType("external")}
            className={`flex-1 rounded-xl border-2 p-4 text-left transition-all ${adType === "external" ? "border-[#E8956B] bg-white" : "border-neutral-200 bg-white hover:border-neutral-300"}`}
          >
            <div className="font-bold text-neutral-900 text-sm">External ad</div>
            <div className="text-xs text-neutral-500 mt-1">Banner linking to your own website.</div>
          </button>
        </div>

        {adType === "product" ? (
          myProducts.length === 0 ? (
            <p className="text-sm text-neutral-500" data-testid="no-listings-msg">
              You don't have any active listings yet. Create a listing first, or use an External ad.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="my-listing-picker">
              {myProducts.map((p) => (
                <button
                  key={p.id}
                  data-testid={`promote-product-${p.id}`}
                  onClick={() => setProductId(p.id)}
                  className={`rounded-xl border-2 overflow-hidden text-left transition-all ${productId === p.id ? "border-[#E8956B]" : "border-neutral-200 hover:border-neutral-300"}`}
                >
                  <div className="aspect-square bg-neutral-100 relative">
                    <img src={imgUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover" />
                    {productId === p.id && (
                      <span className="absolute top-2 right-2 bg-[#E8956B] text-white rounded-full p-1"><Check size={12} /></span>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="text-xs font-semibold text-neutral-900 line-clamp-1">{p.title}</div>
                    <div className="text-xs text-neutral-500">${(p.sale_price || p.price).toFixed(2)}</div>
                  </div>
                </button>
              ))}
            </div>
          )
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-neutral-900">Ad title</label>
              <input
                data-testid="ad-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. 20% off our new brush set"
                className="mt-1 w-full bg-white border border-neutral-300 rounded-xl px-4 py-2.5 outline-none focus:border-[#E8956B]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-neutral-900">Destination link</label>
              <input
                data-testid="ad-url-input"
                value={targetUrl}
                onChange={(e) => setTargetUrl(e.target.value)}
                placeholder="https://your-website.com"
                className="mt-1 w-full bg-white border border-neutral-300 rounded-xl px-4 py-2.5 outline-none focus:border-[#E8956B]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-neutral-900">Banner image (square works best)</label>
              <div className="mt-1 flex items-center gap-4">
                <label data-testid="ad-image-label" className="w-28 h-28 rounded-xl border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#E8956B] transition-colors text-neutral-400 shrink-0 overflow-hidden">
                  {uploading ? <Loader2 size={20} className="animate-spin" /> : image ? (
                    <img src={imgUrl(image)} alt="ad" className="w-full h-full object-cover" />
                  ) : (<><Upload size={20} /><span className="text-[10px] mt-1">Upload</span></>)}
                  <input data-testid="ad-image-input" type="file" accept="image/*" className="hidden" onChange={handleUpload} />
                </label>
                <p className="text-xs text-neutral-500">Your image + title will show as a "Sponsored" card that opens your link in a new tab.</p>
              </div>
            </div>
          </div>
        )}

        <button
          data-testid="ad-checkout-btn"
          onClick={submit}
          disabled={submitting}
          className="mt-7 w-full sm:w-auto bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-8 py-3 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 size={18} className="animate-spin" /> : <Megaphone size={18} />}
          {user ? `Pay $${plans[plan]?.price ?? ""} & publish` : "Sign in to advertise"}
        </button>
      </div>

      {/* MY ADS */}
      {user && myAds.length > 0 && (
        <div data-testid="my-ads-section">
          <h2 className="font-display text-2xl font-bold text-neutral-900 mb-5">My ads</h2>
          <div className="space-y-3">
            {myAds.map((ad) => (
              <div key={ad.id} data-testid={`my-ad-${ad.id}`} className="flex items-center gap-4 bg-white border border-neutral-200 rounded-2xl p-4">
                <div className="w-14 h-14 rounded-lg bg-neutral-100 overflow-hidden shrink-0">
                  {ad.image && <img src={imgUrl(ad.image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-neutral-900 text-sm line-clamp-1">{ad.title || (ad.ad_type === "product" ? "Promoted listing" : "External ad")}</div>
                  <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-2">
                    <span className="capitalize">{ad.ad_type === "product" ? "Listing" : "External"}</span>
                    <span>·</span>
                    <span>${ad.price} / {ad.days}d</span>
                    {ad.expires_at && <><span>·</span><span>ends {new Date(ad.expires_at).toLocaleDateString()}</span></>}
                  </div>
                </div>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLES[ad.status] || "bg-neutral-200 text-neutral-600"}`}>
                  {ad.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
